import { useTranslation } from "react-i18next";
import { useState, useCallback } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { Hero } from "@/components/sections/Hero";
import { Categories } from "@/components/sections/Categories";
import { Navigation } from "@/components/sections/Navigation";
import { ParticipatingEntities } from "@/components/entities/ParticipatingEntities";
import { DynamicCategorySections } from "@/components/sections/DynamicCategorySections";
import { Footer } from "@/components/sections/Footer";
import { useEvents } from "@/contexts/EventsContext";
import { useLiveStream } from "@/contexts/LiveStreamContext";
import { useSectionVisibility } from "@/hooks/useSectionVisibility";
import { Button } from "@/components/ui/button";
import { BannerAd } from "@/components/ads/BannerAd";
import FoundingDayPopup from "@/components/FoundingDayPopup";
import { 
  EventsNewsTicker, 
  CoursesJobsTicker, 
  SocialUpdatesTicker 
} from "@/components/sections/NewsTicker";

const Index = () => {
  const { i18n } = useTranslation();
  const { events } = useEvents();
  const { youtubeUrl } = useLiveStream();
  const { isSectionVisible } = useSectionVisibility();
  const isRTL = i18n.language === "ar";
  const [searchQuery, setSearchQuery] = useState("");
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [registrationType, setRegistrationType] = useState<string>("");
  const [showNewsTickers, setShowNewsTickers] = useState(true);
  const [isBannerVisible, setIsBannerVisible] = useState(true);

  const handleSetSearchQuery = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleRegistrationClick = useCallback(() => {
    console.log("Registration clicked, setting isRegistrationOpen to true");
    setIsRegistrationOpen(true);
  }, []);


  const getEmbedUrl = (url: string) => {
    try {
      if (!url) return '';
      
      if (url.includes('youtu.be/')) {
        const videoId = url.split('youtu.be/')[1].split('?')[0];
        return `https://www.youtube.com/embed/${videoId}`;
      }
      
      if (url.includes('watch?v=')) {
        const videoId = url.split('watch?v=')[1].split('&')[0];
        return `https://www.youtube.com/embed/${videoId}`;
      }
      
      if (url.includes('/embed/')) {
        return url;
      }
      
      return url;
    } catch (error) {
      console.error('Error converting YouTube URL:', error);
      return '';
    }
  };

  const embedUrl = getEmbedUrl(youtubeUrl);

  return (
    <div className={`min-h-screen flex flex-col ${isRTL ? "rtl" : "ltr"}`}>
      <FoundingDayPopup />
      {/* بانر الإعلانات العلوي - ثابت دائماً */}
      <BannerAd onVisibilityChange={setIsBannerVisible} />
      
      {/* مساحة فارغة لتعويض ارتفاع البانر الثابت - تظهر فقط عند ظهور البانر */}
      {isBannerVisible && <div className="h-16 md:h-20" />}
      
      {/* الهيدر وأشرطة الأخبار ثابتة عند التمرير */}
      <div className={`sticky ${isBannerVisible ? 'top-16 md:top-20' : 'top-0'} z-50`}>
        
        <Navigation 
          isRTL={isRTL}
          setRegistrationType={setRegistrationType}
          handleRegistrationClick={handleRegistrationClick}
        />
        
        {/* أشرطة الأخبار - تحت الهيدر مباشرة */}
        {isSectionVisible("news_ticker") && (
          <div className="relative">
            {showNewsTickers && (
              <div className="space-y-0 animate-fade-in">
                <EventsNewsTicker />
                <CoursesJobsTicker />
                <SocialUpdatesTicker />
              </div>
            )}
            
            {/* زر إخفاء/إظهار أشرطة الأخبار */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowNewsTickers(!showNewsTickers)}
              className="absolute -bottom-3 left-1/2 -translate-x-1/2 z-10 h-6 px-3 bg-background border shadow-sm hover:bg-muted rounded-full"
            >
              {showNewsTickers ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              <span className="text-xs mx-1">
                {showNewsTickers 
                  ? (isRTL ? "إخفاء" : "Hide") 
                  : (isRTL ? "إظهار الأخبار" : "Show News")}
              </span>
            </Button>
          </div>
        )}
      </div>
      
      <main id="main-content" tabIndex={-1} className="flex-1 focus:outline-none">
        {isSectionVisible("hero") && (
          <Hero 
            isRTL={isRTL}
            searchQuery={searchQuery}
            setSearchQuery={handleSetSearchQuery}
          />
        )}
        
        {/* قسم تصفح الأقسام */}
        {isSectionVisible("categories") && <Categories isRTL={isRTL} />}
        
        {/* سكشنات جميع الأقسام الديناميكية بالترتيب - تشمل X Spaces و Social Groups */}
        <DynamicCategorySections isRTL={isRTL} />

        {embedUrl && (
          <section className="py-8 bg-gray-50">
            <div className="container mx-auto px-4">
              <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
                {isRTL ? "البث المباشر" : "Live Stream"}
              </h2>
              <div className="relative overflow-hidden rounded-lg shadow-lg aspect-video">
                <iframe
                  src={embedUrl}
                  title="YouTube Live Stream"
                  className="absolute top-0 left-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </section>
        )}

        {isSectionVisible("partners") && <ParticipatingEntities isRTL={isRTL} />}
      </main>
      <Footer />
    </div>
  );
};

export default Index;
